import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TradeCard } from 'store/services/trade/tradeApi';
import './SendCardTooltip.css';

// URL construction helpers
const getSetIconUrl = (code: string): string =>
  `https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons/${code.toLowerCase()}.svg`;

// Display maps
const LANGUAGE_DISPLAY: Record<string, string> = {
  'English': 'English',
  'Spanish': 'Spanish',
  'French': 'French',
  'German': 'German',
  'Italian': 'Italian',
  'Portuguese': 'Portuguese',
  'Japanese': 'Japanese',
  'Korean': 'Korean',
  'Russian': 'Russian',
  'Chinese Simplified': 'Chinese (S)',
  'Chinese Traditional': 'Chinese (T)',
  // Short codes as fallback
  'en': 'EN',
  'es': 'ES',
  'fr': 'FR',
  'de': 'DE',
  'it': 'IT',
  'pt': 'PT',
  'ja': 'JP',
  'ko': 'KO',
  'ru': 'RU',
  'zhs': 'CN',
  'zht': 'TW',
};

const CONDITION_DISPLAY: Record<string, string> = {
  'NEAR_MINT': 'NM',
  'SLIGHTLY_PLAYED': 'SP',
  'MODERATELY_PLAYED': 'MP',
  'HEAVILY_PLAYED': 'HP',
  'DAMAGED': 'DMG',
};

interface SendCardTooltipProps {
  cardData: TradeCard;
  position: { x: number; y: number };
  username: string;
  onClose: () => void;
  onSetHover: (setCode: string | null) => void;
  hoveredSetCode: string | null;
}

export const SendCardTooltip: React.FC<SendCardTooltipProps> = ({
  cardData,
  position,
  username,
  onClose,
  onSetHover,
  hoveredSetCode,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [isVisible, setIsVisible] = useState(false);
  const [failedSetIcons, setFailedSetIcons] = useState<Set<string>>(new Set());

  // Debounce tooltip appearance (200ms delay)
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Edge detection for positioning - flush to card edge (no gap)
  useEffect(() => {
    if (!tooltipRef.current || !isVisible) return;

    const tooltip = tooltipRef.current;
    const rect = tooltip.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Position is already the right edge of the card, so x is flush
    let { x, y } = position;

    // Right edge detection - if tooltip would go off screen, we can't show on left easily
    // Just clamp to viewport
    if (x + rect.width > viewport.width - 10) {
      x = viewport.width - rect.width - 10;
    }

    // Bottom edge detection
    if (y + rect.height > viewport.height - 20) {
      y = viewport.height - rect.height - 20;
    }

    // Top edge detection
    if (y < 20) {
      y = 20;
    }

    setAdjustedPosition({ x, y });
  }, [position, isVisible]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onClose]);

  // Determine finish display
  const finishBadges = useMemo(() => {
    const badges: string[] = [];
    // Use acceptFoil/acceptNonFoil if available, otherwise fall back to availableInFoil/availableInNonFoil
    const acceptsFoil = cardData.acceptFoil ?? cardData.availableInFoil ?? true;
    const acceptsNonFoil = cardData.acceptNonFoil ?? cardData.availableInNonFoil ?? true;

    if (acceptsNonFoil) badges.push('Nonfoil');
    if (acceptsFoil) badges.push('Foil');
    return badges;
  }, [cardData]);

  const handleSetIconError = (code: string) => {
    setFailedSetIcons((prev) => new Set(prev).add(code));
  };

  if (!isVisible) {
    return null;
  }

  const tooltipContent = (
    <div
      className="send-card-tooltip-portal"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      <div className="send-card-tooltip" ref={tooltipRef}>
        {/* Username Header */}
        <div className="tooltip-header">
          <span className="tooltip-username">{username}</span>
          <span className="tooltip-wants"> wants:</span>
        </div>

        {/* Sets Section */}
        <div className="tooltip-section">
          <div className="tooltip-section-label">Sets</div>
          <div className="tooltip-set-icons">
            {cardData.acceptableSetDetails?.length ? (
              <>
                {cardData.acceptableSetDetails.slice(0, 25).map((set) => (
                  !failedSetIcons.has(set.code) && (
                    <img
                      key={set.code}
                      src={getSetIconUrl(set.code)}
                      alt={set.name}
                      title={set.name}
                      className={`tooltip-set-icon ${hoveredSetCode === set.code ? 'hovered' : ''}`}
                      onMouseEnter={() => onSetHover(set.code)}
                      onMouseLeave={() => onSetHover(null)}
                      onError={() => handleSetIconError(set.code)}
                    />
                  )
                ))}
                {cardData.acceptableSetDetails.length > 25 && (
                  <span className="tooltip-more-sets">
                    +{cardData.acceptableSetDetails.length - 25} more
                  </span>
                )}
              </>
            ) : (
              <span className="tooltip-any-badge">Any set</span>
            )}
          </div>
        </div>

        {/* Finishes Section */}
        <div className="tooltip-section">
          <div className="tooltip-section-label">Finishes</div>
          <div className="tooltip-badges">
            {finishBadges.length > 0 ? (
              finishBadges.map((finish) => (
                <span
                  key={finish}
                  className={`tooltip-badge ${finish.toLowerCase()}`}
                >
                  {finish}
                </span>
              ))
            ) : (
              <span className="tooltip-any-badge">Any finish</span>
            )}
          </div>
        </div>

        {/* Languages Section */}
        <div className="tooltip-section">
          <div className="tooltip-section-label">Accepted Languages</div>
          <div className="tooltip-badges">
            {cardData.acceptableLanguages?.length ? (
              cardData.acceptableLanguages.map((lang) => (
                <span key={lang} className="tooltip-badge language">
                  {LANGUAGE_DISPLAY[lang] || lang}
                </span>
              ))
            ) : (
              <span className="tooltip-any-badge">Any language</span>
            )}
          </div>
        </div>

        {/* Conditions Section - only show if restricted */}
        {cardData.acceptableConditions?.length && (
          <div className="tooltip-section">
            <div className="tooltip-section-label">Conditions</div>
            <div className="tooltip-badges">
              {cardData.acceptableConditions.map((cond) => (
                <span key={cond} className="tooltip-badge condition">
                  {CONDITION_DISPLAY[cond] || cond}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export default SendCardTooltip;
